/**
 * Video Reward Manager — F4.8 (APP_FEEL_2027_UPGRADE §11.1)
 *
 * Tracks cumulative correct answers across all sessions and triggers
 * one-shot rewards at symbolic milestones (currently: 100 correct).
 *
 * Per §12.C the original 15-video plan is reduced to **one** symbolic moment
 * to avoid school-WiFi blocking, autoplay restrictions and Chromebook audio
 * variability. The celebration is rendered as an animated SVG (no video file
 * needed) so it works offline and in restricted networks.
 *
 * Storage: localStorage (mirrors the existing dataLayer pattern in
 * src/data/domains). Keys are NOT learner-namespaced yet — when multi-account
 * persistence lands (BACKEND_MIGRATION_PLAN Fase 3b) this manager can be
 * migrated to dataLayer in one move; the public API stays the same.
 */

const CUMULATIVE_CORRECT_KEY = 'meester-djay-cumulative-correct';
const REWARD_SHOWN_PREFIX = 'meester-djay-video-reward-shown:';

/** Symbolic milestone — first (and currently only) reward trigger. */
export const HUNDRED_CORRECT_REWARD = 'hundred_correct' as const;
export type VideoRewardId = typeof HUNDRED_CORRECT_REWARD;

/** Read the cumulative number of correct answers across all sessions. */
export function getCumulativeCorrect(): number {
  try {
    const raw = localStorage.getItem(CUMULATIVE_CORRECT_KEY);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

/** Increment the cumulative correct counter. Returns the new total. */
export function incrementCumulativeCorrect(by: number): number {
  const safe = Math.max(0, Math.floor(by));
  if (safe === 0) return getCumulativeCorrect();
  const prev = getCumulativeCorrect();
  const next = prev + safe;
  try {
    localStorage.setItem(CUMULATIVE_CORRECT_KEY, String(next));
  } catch {

    /* silent — non-fatal */}
  return next;
}

/** Has this one-shot reward already been shown? */
export function isVideoRewardShown(id: VideoRewardId): boolean {
  try {
    return localStorage.getItem(REWARD_SHOWN_PREFIX + id) === '1';
  } catch {
    // Treat unknown as "already shown" — better silent than infinite popups.
    return true;
  }
}

/** Mark a one-shot reward as shown so it never triggers again. */
export function markVideoRewardShown(id: VideoRewardId): void {
  try {
    localStorage.setItem(REWARD_SHOWN_PREFIX + id, '1');
  } catch {

    /* silent */}
}

/**
 * Determine whether the hundred-correct milestone was just crossed by this
 * session. Pure: depends only on the inputs.
 *
 * @param prevTotal cumulative correct BEFORE the session was added
 * @param newTotal  cumulative correct AFTER the session was added
 */
export function didCrossHundred(prevTotal: number, newTotal: number): boolean {
  return prevTotal < 100 && newTotal >= 100;
}

/**
 * High-level helper used by SessionSummary: record this session's correct
 * answers and return whether a one-shot reward should now be shown.
 *
 * Returns `null` when nothing should happen; otherwise the reward id to show.
 */
export function recordSessionAndCheckReward(correctThisSession: number): VideoRewardId | null {
  const prev = getCumulativeCorrect();
  const next = incrementCumulativeCorrect(correctThisSession);
  if (didCrossHundred(prev, next) && !isVideoRewardShown(HUNDRED_CORRECT_REWARD)) {
    return HUNDRED_CORRECT_REWARD;
  }
  return null;
}

/** Test / dev utility — resets all reward state. Not exported via barrel. */
export function _resetVideoRewardState(): void {
  try {
    localStorage.removeItem(CUMULATIVE_CORRECT_KEY);
    localStorage.removeItem(REWARD_SHOWN_PREFIX + HUNDRED_CORRECT_REWARD);
  } catch {

    /* silent */}
}